import React, { useState, useRef } from 'react'
import { Article } from '../types'
import { apiClient } from '../services/api'
import { getSiteName, getArticleTitle } from '../utils/articleUtils'
import { RowThumbnail } from './RowThumbnail'

interface Props {
  article: Article
  onUpdate: (updated: Article) => void
  onDelete: (article: Article) => void
  onTagClick: (tag: string) => void
}

export default function ArticleCard({ article, onUpdate, onDelete, onTagClick }: Props) {
  const [editingTags, setEditingTags] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [sharing, setSharing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const isUnread = !article.readAt

  async function toggleFavorite() {
    try {
      const updated = await apiClient.updateArticle(article.articleId, {
        isFavorite: !article.isFavorite,
      })
      onUpdate(updated)
    } catch (err: unknown) {
      console.error('Failed to toggle favorite:', err)
    }
  }

  async function addTag() {
    const tag = tagInput.trim().toLowerCase()
    if (!tag || article.tags.includes(tag)) {
      setTagInput('')
      return
    }
    try {
      const updated = await apiClient.updateArticle(article.articleId, {
        tags: [...article.tags, tag],
      })
      onUpdate(updated)
      setTagInput('')
    } catch (err: unknown) {
      console.error('Failed to add tag:', err)
    }
  }

  async function removeTag(tag: string) {
    try {
      const updated = await apiClient.updateArticle(article.articleId, {
        tags: article.tags.filter((t) => t !== tag),
      })
      onUpdate(updated)
    } catch (err: unknown) {
      console.error('Failed to remove tag:', err)
    }
  }

  /**
   * Shares the article. For email articles, first creates a share link via the
   * API then invokes the native share sheet (with clipboard fallback). For URL
   * articles, shares the original URL directly via the native share sheet.
   */
  async function handleShare() {
    setSharing(true)
    try {
      let shareUrl: string
      let title = article.title || ''

      if (isEmail) {
        const result = await apiClient.createShareToken(article.articleId)
        shareUrl = result.shareUrl
      } else {
        shareUrl = article.url || ''
      }

      try {
        await navigator.share({ title, url: shareUrl })
      } catch (e: unknown) {
        // AbortError means the user dismissed the share sheet — not an error.
        if (e instanceof Error && e.name === 'AbortError') return
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl)
      }
    } catch (err: unknown) {
      console.error('Failed to share article:', err)
    } finally {
      setSharing(false)
    }
  }

  function handleTagKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    }
    if (e.key === 'Escape') {
      setEditingTags(false)
      setTagInput('')
    }
  }

  const isEmail = article.source === 'email'
  const siteName = getSiteName(article)
  const title = getArticleTitle(article)
  const date = new Date(article.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className={`article-card${isUnread ? ' unread' : ''}`}>
      <div className="article-card-inner">
        <RowThumbnail imageUrl={article.imageUrl} className="article-card-thumb" />
        <div className="article-card-body">
          <div className="article-card-meta">
            <span>{siteName}</span>
            <span className="meta-dot">·</span>
            <span>{date}</span>
            {!isEmail && article.author && (
              <>
                <span className="meta-dot">·</span>
                <span>{article.author}</span>
              </>
            )}
            {isEmail && article.fromEmail && (
              <>
                <span className="meta-dot">·</span>
                <span>{article.fromEmail}</span>
              </>
            )}
          </div>
          <div className="article-card-title-row">
            <span className="unread-dot" aria-hidden="true" />
            <a
              className="article-card-title"
              href={isEmail ? `/articles/${article.articleId}` : article.url || undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              {title}
            </a>
          </div>
          {article.excerpt && <p className="article-card-excerpt">{article.excerpt}</p>}
          <div className="article-card-tags">
            {article.tags.map((tag) => (
              <span key={tag} className="tag tag-clickable" onClick={() => onTagClick(tag)}>
                {tag}
                <button
                  className="tag-remove"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeTag(tag)
                  }}
                  title="Remove tag"
                >
                  ×
                </button>
              </span>
            ))}
            {editingTags ? (
              <input
                ref={inputRef}
                className="tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={() => {
                  if (!tagInput) setEditingTags(false)
                }}
                placeholder="add tag..."
                autoFocus
              />
            ) : (
              <button className="tag-add" onClick={() => setEditingTags(true)} title="Add tag">
                + tag
              </button>
            )}
          </div>
        </div>

        <div className="article-card-actions">
          <button
            className={`btn-ghost${article.isFavorite ? ' active' : ''}`}
            onClick={toggleFavorite}
            title={article.isFavorite ? 'Unfavorite' : 'Favorite'}
            aria-label={article.isFavorite ? 'Unfavorite' : 'Favorite'}
            aria-pressed={article.isFavorite}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill={article.isFavorite ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
          </button>
          <button
            className="btn-ghost"
            onClick={handleShare}
            disabled={sharing}
            title="Share"
            aria-label="Share"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z"
              />
            </svg>
          </button>
          <button
            className="btn-danger"
            onClick={() => onDelete(article)}
            title="Delete"
            aria-label="Delete"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
